import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, KeyRound, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Missing reset token. Request a new link from the Forgot Password page.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1600);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Reset failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Account</div>
        <h1 className="font-display font-black text-3xl uppercase tracking-tight text-tmf-navy mb-6">Set a new password</h1>

        {done ? (
          <div data-testid="reset-done" className="border border-green-500 bg-green-50 p-6 text-center">
            <CheckCircle2 className="text-green-600 mx-auto mb-3" size={40} />
            <div className="font-display font-bold text-tmf-navy text-lg uppercase tracking-tight mb-1">Password updated</div>
            <p className="text-sm text-slate-700">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">New Password</label>
              <div className="relative mt-1">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="reset-password"
                  type="password"
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  className="rounded-none pl-9"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Confirm Password</label>
              <div className="relative mt-1">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="reset-password-confirm"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Repeat"
                  className="rounded-none pl-9"
                />
              </div>
            </div>
            {err && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-2">
                <XCircle size={16} className="shrink-0 mt-0.5" /><span>{err}</span>
              </div>
            )}
            <Button data-testid="reset-submit" type="submit" disabled={busy || !token} className="rounded-none w-full h-12 bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-sm font-bold">
              {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> Updating…</> : "Update Password"}
            </Button>
            <div className="text-center text-sm text-slate-500 pt-2">
              <Link to="/login" className="text-tmf-blue font-bold hover:underline">Back to Sign In</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
